import type { OpenAIChatKit } from "@openai/chatkit";
import {
  ChatKit,
  type SupportedLocale,
  type UseChatKitOptions,
  useChatKit,
} from "@openai/chatkit-react";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useAuthStore } from "@krea/shared/features/auth/authStore";

import { buildUrl } from "./buildUrl";

const CHATKIT_CDN =
  "https://cdn.platform.openai.com/deployments/chatkit/chatkit.js";
const CHATKIT_SCRIPT_ID = "openai-chatkit-script";
const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

const isAlreadyLoaded = () =>
  typeof customElements !== "undefined" &&
  customElements.get("openai-chatkit") !== undefined;

const useChatKitScript = () => {
  const [loaded, setLoaded] = useState(isAlreadyLoaded);

  useEffect(() => {
    if (loaded) return;

    const existing = document.getElementById(
      CHATKIT_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existing) {
      const onLoad = () => setLoaded(true);
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.id = CHATKIT_SCRIPT_ID;
    script.src = CHATKIT_CDN;
    script.async = true;
    const onLoad = () => setLoaded(true);
    script.addEventListener("load", onLoad);
    document.head.appendChild(script);
    return () => script.removeEventListener("load", onLoad);
  }, [loaded]);

  return loaded;
};

interface IPersistedChatState {
  /** Unix timestamp (ms) of the last thread interaction — used for the 15-min window check. */
  threadTimestamp?: number;
}

const readPersisted = (key: string): IPersistedChatState | null => {
  try {
    const raw = sessionStorage.getItem(`chatkit.${key}`);
    return raw ? (JSON.parse(raw) as IPersistedChatState) : null;
  } catch {
    return null;
  }
};

const writePersisted = (
  key: string,
  patch: Partial<IPersistedChatState>,
): void => {
  try {
    const existing = readPersisted(key) ?? {};
    sessionStorage.setItem(
      `chatkit.${key}`,
      JSON.stringify({ ...existing, ...patch }),
    );
  } catch {}
};

export type AIChatFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export type ChatKitHandle = {
  sendUserMessage: (params: {
    text?: string;
    newThread?: boolean;
  }) => Promise<void>;
  setThreadId: (id: string | null) => Promise<void>;
  setComposerValue: (params: { text?: string }) => Promise<void>;
  focusComposer: () => void;
};

interface IChatWindowProps {
  endpoint: string;
  countryCode: string;
  locale: SupportedLocale;
  title?: string;
  placeholder?: string;
  customFetch?: AIChatFetch;
  /**
   * Extra headers merged into every chat request — the pre-flight AND every ChatKit turn —
   * on top of `Authorization`. Use for per-surface entity headers the backend reads from the
   * request (e.g. the lender Case Assistant's `X-Lender-Application-Id`). Ignored when
   * `customFetch` fully replaces the transport.
   */
  requestHeaders?: Record<string, string>;
  /**
   * How the initial thread is resolved on mount:
   * - "threads-list" (default): POST a `threads.list` pre-flight and resume the latest thread
   *   when the last interaction (per `persistKey`) was within 15 minutes.
   * - "session-start": POST once with `X-Chat-Session-Start: 1`; the backend returns
   *   `{ threadId }` — the server-scoped thread to resume, or `null` to start fresh. Use for
   *   surfaces where the server owns per-case thread scoping (lender Case Assistant), which
   *   must NOT use an unscoped `threads.list`.
   */
  resumeStrategy?: "threads-list" | "session-start";
  /**
   * When provided, the last-active timestamp is persisted under this key; on remount the
   * latest server thread is resumed if the last interaction was within 15 minutes.
   */
  persistKey?: string;
  /**
   * Passthrough options merged into useChatKit. `api` is always controlled by
   * ChatWindow. `onThreadChange` is also always wired by ChatWindow (for
   * `persistKey` bookkeeping) but is additionally forwarded to
   * `options.onThreadChange` if provided, so a consumer can learn the active
   * thread id without owning persistence itself. `onReady` is forwarded as-is.
   */
  options?: Partial<UseChatKitOptions>;
  /** Fires once the ChatKit frame is ready, exposing imperative methods to drive it. */
  onReady?: (handle: ChatKitHandle) => void;
  /**
   * Fires on any chat failure. `meta.phase` distinguishes the source:
   * - "preflight": the threads.list pre-flight request failed (e.g. backend 400/403/404).
   * - "ready-timeout": ChatKit didn't become ready within `readyTimeout` (CDN/iframe never loaded).
   * - "runtime": ChatKit reported an error after becoming ready (mid-session send/stream failure).
   */
  onError?: (
    error: unknown,
    meta?: {
      status?: number;
      phase?: "preflight" | "ready-timeout" | "runtime";
    },
  ) => void;
  /**
   * If set, `onError` fires with phase "ready-timeout" when ChatKit doesn't signal ready
   * within this many ms of mount — guards a silent CDN/iframe load failure. Opt-in per app.
   */
  readyTimeout?: number;
  /**
   * When provided, skips the threads.list fetch and uses this value directly
   * as initialThread. Pass null to start fresh, a string to resume a thread.
   * Omit (undefined) for the default threads.list resolution.
   */
  initialThreadOverride?: string | null;
  /**
   * When provided, forwarded to the underlying ChatKit element so callers
   * can call sendCustomAction() imperatively.
   */
  chatkitRef?: RefObject<OpenAIChatKit | null>;
}

interface IChatKitWidgetProps extends IChatWindowProps {
  initialThread: string | null;
}

export type { UseChatKitOptions };

const ChatKitWidget = ({
  endpoint,
  countryCode,
  locale,
  title,
  placeholder,
  customFetch,
  requestHeaders,
  persistKey,
  initialThread,
  options,
  onReady,
  onError,
  chatkitRef,
}: IChatKitWidgetProps) => {
  const accessToken = useAuthStore((s) => s.accessToken);

  // Track the active SSE connection for the chat endpoint so we can abort the previous turn
  // before opening a new one — prevents hitting the browser's per-origin connection limit
  // (HTTP/1.1 allows 6; after ~5 unaborted streams the 6th request hangs indefinitely).
  const sseAbortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      sseAbortRef.current?.abort();
    },
    [],
  );

  const fetchWithHeaders: AIChatFetch = (url, init) => {
    if (customFetch) return customFetch(url, init);
    // Abort the previous chat SSE before opening a new one. Only the chat endpoint produces
    // long-lived streaming connections; upload requests go to different URLs (presigned S3).
    const isChatStream = String(url).startsWith(endpoint);
    let signal = init?.signal;
    if (isChatStream) {
      sseAbortRef.current?.abort();
      const ctrl = new AbortController();
      sseAbortRef.current = ctrl;
      signal = ctrl.signal;
    }
    return fetch(url, {
      ...init,
      signal,
      headers: {
        // Normalize init.headers: ChatKit can pass a Headers instance, which doesn't spread
        // correctly as a plain object — Object.fromEntries ensures all entries are preserved.
        ...Object.fromEntries(new Headers(init?.headers)),
        ...requestHeaders,
        // Authorization must be last so requestHeaders can never accidentally override the token.
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
  };

  // @openai/chatkit-react's <ChatKit> component owns all event listeners on the
  // element and routes them to onXxx callbacks via EVENT_HANDLER_MAP. Direct
  // el.addEventListener calls compete with that layer and are unreliable.
  // Use the option callbacks instead — they're the intended API for the React wrapper.
  //
  // chatkit.setComposerValue is useChatKit's null-safe wrapped method — prefer it
  // over chatkit.ref.current?.setComposerValue which can be null if called too early.
  const chatkit = useChatKit({
    ...options,
    api: {
      url: buildUrl(endpoint, countryCode),
      domainKey: "domain_pk_69cd241675908196b717e6e77c3388ee0aaddfe7ab2273f7",
      fetch: fetchWithHeaders,
      uploadStrategy: { type: "two_phase" },
    },
    locale,
    // Pass null directly (not undefined). `null` = new-thread view; `undefined`
    // = auto-resolve, which can attach to the wrong existing thread.
    initialThread,
    startScreen: {
      ...options?.startScreen,
      greeting: title ?? "Assistant",
      prompts: options?.startScreen?.prompts ?? [],
    },
    composer: {
      placeholder: placeholder ?? "Type a message...",
      attachments: { enabled: true },
      ...options?.composer,
    },
    onReady: () => {
      options?.onReady?.();
      onReady?.({
        sendUserMessage: chatkit.sendUserMessage,
        setThreadId: chatkit.setThreadId,
        setComposerValue: chatkit.setComposerValue,
        focusComposer: chatkit.focusComposer,
      });
    },
    onThreadChange: (event) => {
      if (persistKey && event.threadId) {
        writePersisted(persistKey, { threadTimestamp: Date.now() });
      }
      options?.onThreadChange?.(event);
    },
    // Surface ChatKit's own post-ready runtime errors (send/stream failures) to the consumer.
    onError: (event) => {
      onError?.(event.error, { phase: "runtime" });
    },
    // Opt-in only: a default logger here would emit every chat effect payload
    // (user/thread metadata) to the browser console in production.
    onEffect: options?.onEffect,
    widgets: options?.widgets,
  });

  // useChatKit only honors `initialThread` on mount, so subsequent prop
  // changes (e.g. parent resolves a thread asynchronously) must be applied
  // imperatively. chatkit.setThreadId is the null-safe wrapped method;
  // passing null shows the new-thread view.
  const lastAppliedThreadRef = useRef<string | null | undefined>(initialThread);
  useEffect(() => {
    if (initialThread === lastAppliedThreadRef.current) return;
    lastAppliedThreadRef.current = initialThread;
    void chatkit.setThreadId(initialThread);
  }, [initialThread, chatkit]);

  return (
    <ChatKit
      control={chatkit.control}
      className="block h-full w-full"
      ref={chatkitRef ?? undefined}
    />
  );
};

export const ChatWindow = ({
  endpoint,
  countryCode,
  locale,
  title,
  placeholder,
  customFetch,
  requestHeaders,
  resumeStrategy,
  persistKey,
  options,
  onReady,
  onError,
  readyTimeout,
  initialThreadOverride,
  chatkitRef,
}: IChatWindowProps) => {
  const scriptLoaded = useChatKitScript();
  const accessToken = useAuthStore((s) => s.accessToken);

  // string = thread to resume, null = start fresh, undefined = still resolving
  const [resolvedThread, setResolvedThread] = useState<
    string | null | undefined
  >(initialThreadOverride !== undefined ? initialThreadOverride : undefined);

  // CRITICAL: keep resolvedThread in sync with initialThreadOverride. Without
  // this, the useState initializer only sees the first value, and subsequent
  // prop updates (e.g. parent setting thread_id after a resume/start fetch)
  // are silently ignored — ChatKit stays mounted on the original new-thread
  // view forever.
  useEffect(() => {
    if (initialThreadOverride !== undefined) {
      setResolvedThread(initialThreadOverride);
      return;
    }
    // Override cleared → drop back to "resolving" so the old thread stops
    // rendering and the threads.list fetch below re-runs to pick the default.
    setResolvedThread(undefined);
  }, [initialThreadOverride]);

  // Stable refs so the fetch effect doesn't re-run on every render.
  const customFetchRef = useRef(customFetch);
  customFetchRef.current = customFetch;
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const requestHeadersRef = useRef(requestHeaders);
  requestHeadersRef.current = requestHeaders;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Backstop for a silently never-ready ChatKit (CDN script or iframe handshake never completes,
  // so neither the pre-flight nor a runtime error ever fires). Armed on mount; cleared on ready.
  const readyFiredRef = useRef(false);
  useEffect(() => {
    if (readyTimeout == null) return;
    const id = window.setTimeout(() => {
      if (!readyFiredRef.current) {
        onErrorRef.current?.(new Error("chatkit ready timeout"), {
          phase: "ready-timeout",
        });
      }
    }, readyTimeout);
    return () => window.clearTimeout(id);
  }, [readyTimeout]);

  const handleReady = useCallback(
    (handle: ChatKitHandle) => {
      readyFiredRef.current = true;
      onReady?.(handle);
    },
    [onReady],
  );

  // The pre-flight only depends on the resolved request URL, so derive it once and key
  // the effect on the string. It then re-runs exactly when the URL changes — for any
  // app's context fields — instead of on every parent re-render (callers pass an inline
  // context object) or only the subset of fields that happened to be listed by hand.
  const url = buildUrl(endpoint, countryCode);
  // Serialise requestHeaders so an inline object's identity doesn't retrigger the effect on
  // every parent render — it re-runs only when the actual header values (or url/strategy) change.
  const requestHeadersKey = JSON.stringify(requestHeaders ?? null);

  // requestHeadersKey is listed in the deps on purpose so the pre-flight re-runs when entity
  // header values change — they're read from requestHeadersRef inside, so the linter can't see it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional extra dep (see above).
  useEffect(() => {
    if (initialThreadOverride !== undefined) return;

    // Guard against out-of-order resolution: if deps change (or the component
    // unmounts) before this request settles, an older response must not
    // overwrite the newer state.
    let cancelled = false;
    const controller = new AbortController();

    const doFetch = (u: string, init?: RequestInit) => {
      if (customFetchRef.current) return customFetchRef.current(u, init);
      const token = accessTokenRef.current;
      return fetch(u, {
        ...init,
        headers: {
          ...init?.headers,
          ...requestHeadersRef.current,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    };

    if (resumeStrategy === "session-start") {
      // Server-owned per-case resume: one X-Chat-Session-Start request returns the scoped
      // thread id (or null) for THIS case. No threads.list — scoping is the server's job,
      // which is what keeps another case's history from bleeding in. The entity headers (in
      // requestHeaders, e.g. X-Lender-Application-Id, X-Lender-Company-Id) ride along via doFetch.
      doFetch(url, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "X-Chat-Session-Start": "1",
        },
        body: "{}",
      })
        .then(async (r) => {
          if (!r.ok) {
            onErrorRef.current?.(
              new Error(`chatkit session-start ${r.status}`),
              { status: r.status, phase: "preflight" },
            );
            if (!cancelled) setResolvedThread(null);
            return null;
          }
          return r.json() as Promise<{ threadId: string | null }>;
        })
        .then((data) => {
          if (!data || cancelled) return;
          setResolvedThread(data.threadId ?? null);
        })
        .catch((e) => {
          if (!cancelled) {
            onErrorRef.current?.(e, { phase: "preflight" });
            setResolvedThread(null);
          }
        });
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    doFetch(url, {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "threads.list",
        params: { limit: 1, order: "desc" },
      }),
    })
      .then(async (r) => {
        if (!r.ok) {
          onErrorRef.current?.(new Error(`chatkit threads.list ${r.status}`), {
            status: r.status,
            phase: "preflight",
          });
          if (!cancelled) setResolvedThread(null);
          return null;
        }
        return r.json() as Promise<{ data: { id: string }[] }>;
      })
      .then((data) => {
        if (!data || cancelled) return;
        const latestThread = data.data[0];
        if (!latestThread || !persistKey) {
          setResolvedThread(null);
          return;
        }
        const persisted = readPersisted(persistKey);
        const isRecent =
          !!persisted?.threadTimestamp &&
          Date.now() - persisted.threadTimestamp < FIFTEEN_MINUTES_MS;
        setResolvedThread(isRecent ? latestThread.id : null);
      })
      .catch((e) => {
        if (!cancelled) {
          onErrorRef.current?.(e, { phase: "preflight" });
          setResolvedThread(null);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    url,
    persistKey,
    resumeStrategy,
    requestHeadersKey,
    initialThreadOverride,
  ]);

  if (!scriptLoaded || resolvedThread === undefined) return null;

  // Pre-flight failures (preflight) and the never-ready backstop (ready-timeout) fire from
  // ChatWindow above; ChatKitWidget additionally forwards ChatKit's runtime errors (runtime).
  return (
    <ChatKitWidget
      endpoint={endpoint}
      countryCode={countryCode}
      locale={locale}
      title={title}
      placeholder={placeholder}
      customFetch={customFetch}
      requestHeaders={requestHeaders}
      persistKey={persistKey}
      initialThread={resolvedThread}
      options={options}
      onReady={handleReady}
      onError={onError}
      chatkitRef={chatkitRef}
    />
  );
};
